import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../UserContext';
import axios from '../axiosInstance';
import {
  FaExclamationTriangle, FaUserShield, FaClock, FaFolderOpen, FaInfoCircle
} from 'react-icons/fa';
import '../Pages.css';
import '../Pips/Pips.css';   // shared design system
import './TM.css';

const SEVERITY_LABEL = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };
const DISPOSITION_LABEL = {
  pending:        'Pending review',
  escalated:      'Escalated',
  false_positive: 'False positive',
  no_action:      'No action'
};

const fmt = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm}/${yyyy}, ${hh}:${mi}`;
};

const money = (amount, currency) => {
  if (amount == null) return '—';
  return `${currency || 'NAD'} ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const hasPermission = (user, name) =>
  !!user && (
    user.is_system_admin === true ||
    (Array.isArray(user.permissions) && user.permissions.includes(name))
  );

const customerName = (a) =>
  a.customer_name_individual || a.customer_name_business || `Customer #${a.customer_id}`;

function ManageAlerts() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const canRead   = hasPermission(user, 'tm_alerts.read');
  const canReview = hasPermission(user, 'tm_alerts.review');

  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({ disposition: 'pending', severity: 'all', query: '' });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [actionModal, setActionModal] = useState(null);
  const [actionVals, setActionVals] = useState({});
  const [actionBusy, setActionBusy] = useState(false);

  const fetchStats = useCallback(async () => {
    try { const r = await axios.get('/tm-alerts/stats'); setStats(r.data); }
    catch (e) { console.error(e.message); }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams();
      p.set('disposition', filter.disposition);
      if (filter.severity !== 'all') p.set('severity', filter.severity);
      if (filter.query.trim())       p.set('query', filter.query.trim());
      p.set('limit', '500');
      const r = await axios.get(`/tm-alerts?${p.toString()}`);
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e.message || 'Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!canRead) return;
    fetchStats();
    fetchList();
  }, [canRead, fetchStats, fetchList]);

  const openDetail = async (id) => {
    setSelectedId(id);
    setDetail(null);
    try {
      const r = await axios.get(`/tm-alerts/${id}`);
      setDetail(r.data);
    } catch (e) {
      setError(e.message || 'Failed to load alert');
      setSelectedId(null);
    }
  };

  const openModal = (cfg) => { setActionVals(cfg.initial || {}); setActionModal(cfg); };

  const runAction = async () => {
    if (!actionModal?.onConfirm) return;
    try {
      setActionBusy(true);
      await actionModal.onConfirm(actionVals);
      setActionModal(null);
      setActionVals({});
      if (selectedId) {
        const r = await axios.get(`/tm-alerts/${selectedId}`);
        setDetail(r.data);
      }
      fetchList();
      fetchStats();
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message || 'Action failed');
    } finally {
      setActionBusy(false);
    }
  };

  const dispose = (disposition, requireNotes = false) => () => openModal({
    title: `Mark alert as ${DISPOSITION_LABEL[disposition]}`,
    fields: [{ key: 'notes', label: requireNotes ? 'Notes (required)' : 'Notes (optional)', type: 'textarea' }],
    onConfirm: async (v) => {
      if (requireNotes && !(v.notes || '').trim()) throw new Error('Notes are required');
      await axios.post(`/tm-alerts/${selectedId}/dispose`, {
        disposition,
        notes: (v.notes || '').trim() || undefined
      });
    }
  });

  if (user && !canRead) {
    return (
      <div className="page-container">
        <div className="alert-banner alert-banner--warning" style={{ marginTop: '2rem' }}>
          <span className="alert-banner__body">
            <FaInfoCircle /> You don't have permission to view TM Alerts.
            Ask your administrator for the <code>tm_alerts.read</code> permission.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Stat cards */}
      {user && (
        <div className="stats-section">
          {/* Shared stat-card layout (Pips.css): compact cards */}
          <div className="stats-layout__rest">
          <div className="stats-grid">
            <div className="stat-card stat-card--teal">
              <div className="stat-label">Total alerts</div>
              <div className="stat-value stat-value--teal">{stats ? stats.total : '—'}</div>
            </div>
            <div className="stat-card stat-card--orange">
              <div className="stat-label">Pending</div>
              <div className="stat-value stat-value--orange">{stats ? stats.pending : '—'}</div>
            </div>
            <div className="stat-card stat-card--purple">
              <div className="stat-label">Critical / High pending</div>
              <div className="stat-value stat-value--purple">
                {stats ? ((stats.pending_by_severity?.critical || 0) + (stats.pending_by_severity?.high || 0)) : '—'}
              </div>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Filters */}
      <section className="form-section" style={{ marginBottom: '1rem' }}>
        <header className="form-section__head">
          <h4 className="form-section__title">Alert queue</h4>
          <p className="form-section__subtitle">Transaction Monitoring alerts surfaced by rule hits. Triage, escalate, or dismiss.</p>
        </header>
        <div className="form-grid-3">
          <div className="form-group">
            <label>Disposition</label>
            <select value={filter.disposition} onChange={e => setFilter(f => ({ ...f, disposition: e.target.value }))}>
              <option value="pending">Pending</option>
              <option value="escalated">Escalated</option>
              <option value="false_positive">False positive</option>
              <option value="no_action">No action</option>
              <option value="all">All</option>
            </select>
          </div>
          <div className="form-group">
            <label>Severity</label>
            <select value={filter.severity} onChange={e => setFilter(f => ({ ...f, severity: e.target.value }))}>
              <option value="all">All severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div className="form-group">
            <label>Search</label>
            <input type="text" placeholder="reason / rule name"
                   value={filter.query}
                   onChange={e => setFilter(f => ({ ...f, query: e.target.value }))}
                   onKeyDown={e => { if (e.key === 'Enter') fetchList(); }} />
          </div>
          <div className="form-group">
            <label>&nbsp;</label>
            <button className="export-button" onClick={fetchList} disabled={loading}>
              {loading ? 'Loading…' : 'Apply filters'}
            </button>
          </div>
        </div>
      </section>

      {error && (
        <div className="alert-banner alert-banner--danger" style={{ marginBottom: '1rem' }}>
          <span className="alert-banner__body"><FaInfoCircle /> {error}</span>
        </div>
      )}

      {/* Alerts list */}
      <div className="tm-alert-list">
        {rows.length === 0 && !loading && (
          <p style={{ color: 'var(--text-secondary,#607d8b)' }}>No alerts match the current filters.</p>
        )}
        {rows.map(a => (
          <div key={a.id}
               className={`tm-alert-card sev-${a.severity} ${a.disposition !== 'pending' ? 'is-resolved' : ''}`}
               onClick={() => openDetail(a.id)}>
            <div className="tm-alert-card__top">
              <div style={{ flex: 1 }}>
                <div className="tm-alert-card__title">{a.rule_name}</div>
                <div className="tm-alert-card__reason">{a.reason}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', alignItems: 'flex-end' }}>
                <span className={`tm-pill tm-pill--sev-${a.severity}`}>{SEVERITY_LABEL[a.severity]}</span>
                <span className={`tm-pill tm-pill--disp-${a.disposition}`}>{DISPOSITION_LABEL[a.disposition]}</span>
                <span className="tm-pill">Score {a.score}</span>
              </div>
            </div>
            <div className="tm-alert-card__meta">
              <span><FaUserShield /> {customerName(a)}</span>
              <span>{a.tx_direction} · {money(a.tx_amount, a.tx_currency)} · {a.tx_channel || '—'}</span>
              <span><FaClock /> {fmt(a.tx_occurred_at)}</span>
              {a.case_number && <span><FaFolderOpen /> {a.case_number}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Detail modal */}
      {selectedId && (
        <div className="modal-overlay" onClick={() => setSelectedId(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <button className="modal-close" onClick={() => setSelectedId(null)}>×</button>
            {!detail ? <div>Loading…</div> : (
              <>
                <h2 style={{ marginTop: 0 }}>
                  <FaExclamationTriangle style={{ marginRight: '0.4rem', color: '#ef6c00' }} />
                  {detail.rule_name}
                </h2>
                <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                  <span className={`tm-pill tm-pill--sev-${detail.severity}`}>{SEVERITY_LABEL[detail.severity]}</span>
                  <span className={`tm-pill tm-pill--disp-${detail.disposition}`}>{DISPOSITION_LABEL[detail.disposition]}</span>
                  <span className="tm-pill">Score {detail.score}</span>
                  {detail.case_number && (
                    <button className="btn-cancel" style={{ padding: '0.2rem 0.55rem', fontSize: '0.7rem' }}
                            onClick={() => navigate(`/cases?id=${detail.case_id}`)}>
                      <FaFolderOpen /> {detail.case_number}
                    </button>
                  )}
                </div>

                <section className="form-section">
                  <header className="form-section__head">
                    <h4 className="form-section__title">Reason</h4>
                  </header>
                  <p>{detail.reason}</p>
                </section>

                <section className="form-section">
                  <header className="form-section__head">
                    <h4 className="form-section__title">Transaction</h4>
                  </header>
                  <div className="form-grid-2">
                    <div><strong>Direction:</strong> {detail.direction}</div>
                    <div><strong>Amount:</strong> {money(detail.amount, detail.currency)}</div>
                    <div><strong>Channel:</strong> {detail.channel || '—'}</div>
                    <div><strong>Occurred:</strong> {fmt(detail.occurred_at)}</div>
                    <div><strong>Counterparty:</strong> {detail.counterparty_name || '—'}</div>
                    <div><strong>Country:</strong> {detail.counterparty_country || '—'}</div>
                    {detail.purpose && <div style={{ gridColumn: '1 / -1' }}><strong>Purpose:</strong> {detail.purpose}</div>}
                  </div>
                </section>

                {detail.evidence && Object.keys(detail.evidence).length > 0 && (
                  <section className="form-section">
                    <header className="form-section__head">
                      <h4 className="form-section__title">Evidence</h4>
                    </header>
                    <pre style={{ background: 'var(--bg-tertiary,#f5f7fa)', padding: '0.6rem', overflow: 'auto', fontSize: '0.75rem' }}>
                      {JSON.stringify(detail.evidence, null, 2)}
                    </pre>
                  </section>
                )}

                {detail.disposition !== 'pending' && (
                  <section className="form-section">
                    <header className="form-section__head">
                      <h4 className="form-section__title">Disposition</h4>
                    </header>
                    <p>
                      <strong>{DISPOSITION_LABEL[detail.disposition]}</strong> by {detail.disposition_by_email || '—'}
                      {' '}on {fmt(detail.disposition_at)}
                    </p>
                    {detail.disposition_notes && <p>{detail.disposition_notes}</p>}
                  </section>
                )}

                {canReview && detail.disposition === 'pending' && (
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button className="export-button" onClick={dispose('escalated', true)}>
                      Escalate &amp; open case
                    </button>
                    <button className="btn-cancel" onClick={dispose('false_positive')}>
                      False positive
                    </button>
                    <button className="btn-cancel" onClick={dispose('no_action')}>
                      No action
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Action modal */}
      {actionModal && (
        <div className="modal-overlay" onClick={() => !actionBusy && setActionModal(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <button className="modal-close" onClick={() => !actionBusy && setActionModal(null)}>×</button>
            <h3 style={{ marginTop: 0 }}>{actionModal.title}</h3>
            {(actionModal.fields || []).map(f => (
              <div key={f.key} className="form-group" style={{ marginBottom: '0.75rem' }}>
                <label>{f.label}</label>
                {f.type === 'textarea' ? (
                  <textarea rows={4}
                            value={actionVals[f.key] || ''}
                            onChange={e => setActionVals(v => ({ ...v, [f.key]: e.target.value }))} />
                ) : (
                  <input type="text"
                         value={actionVals[f.key] || ''}
                         onChange={e => setActionVals(v => ({ ...v, [f.key]: e.target.value }))} />
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" disabled={actionBusy} onClick={() => setActionModal(null)}>Cancel</button>
              <button className="export-button" disabled={actionBusy} onClick={runAction}>
                {actionBusy ? 'Working…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageAlerts;
